import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Users, Zap, Waves, Tent } from 'lucide-react';

const faqs = [
  {
    q: "Is Rudragarh safe and suitable for young children?",
    a: "Yes. Rudragarh is a fully enclosed property with attentive staff on-site throughout the day. The kids' play zone is purpose-built for younger guests, the swimming pool is supervised, and all adventure activities — ATV, horse riding, archery — come with appropriate safety gear and staff guidance. Age-appropriate options are available for every activity so that even the youngest guests have something made for them."
  },
  {
    q: "What activities are there for kids at Rudragarh?",
    a: "Plenty. The dedicated kids' play zone keeps younger children entertained between bigger adventures. The swimming pool is a daytime favourite. Camel safari and horse riding are classic Jaisalmer experiences that children love. ATV rides on the dunes are for older kids and teenagers. Rain dance, football, archery, and air gun shooting round out the activity list. In the evenings, cultural programs and the open-air theatre make for memorable family nights together."
  },
  {
    q: "What rooms are best for families?",
    a: "The The Heritage Huts is our most popular family room — two levels, two double beds, and space for up to 4 guests in an earthy, adventurous setting that children find exciting. The Grand Quad is the most spacious option with 4-guest capacity and a king bed. For larger families, two Cascade Cottagess can be booked in adjacent units — each sleeping 2, giving a family of 4 two private spaces with connecting access."
  }
];

const highlights = [
  {
    icon: <Zap size={28} />,
    title: "ATV & Adventure for All Ages",
    desc: "ATV rides on real desert dunes, camel safari, horse riding, archery, football, and air gun shooting — enough to keep every age group fully occupied."
  },
  {
    icon: <Users size={28} />,
    title: "Kids' Play Zone",
    desc: "A purpose-built play area means young children have their own space to enjoy — no boredom, no 'what do we do next?' moments."
  },
  {
    icon: <Waves size={28} />,
    title: "Pool & Rain Dance",
    desc: "The swimming pool and rain dance floor are family favourites — cool down in the afternoon, then dance in the rain as the sun sets over the dunes."
  },
  {
    icon: <Tent size={28} />,
    title: "The Heritage Huts — Family Favourite",
    desc: "Two levels, two beds, four guests. The bamboo duplex is earthy and adventurous — children find it thrilling, parents find it comfortable."
  }
];

const rooms = [
  {
    slug: "heritage-huts",
    name: "The Heritage Huts",
    price: 15000,
    guests: 4,
    category: "unique",
    desc: "Two levels of bamboo-built adventure. Two double beds, plenty of space, and a setting that children will talk about for years."
  },
  {
    slug: "grand-quad",
    name: "The Grand Quad",
    price: 7000,
    guests: 4,
    category: "modern",
    desc: "The most spacious room at Rudragarh — a king bed, 4-guest capacity, and modern comfort for families who want room to breathe."
  },
  {
    slug: "cascade-cottages",
    name: "Cascade Cottages",
    price: 12000,
    guests: 2,
    category: "heritage",
    desc: "Four standalone cottages available — book adjacent units for a family of 4 across two private connected spaces."
  }
];

const Family = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Family Resort in Jaisalmer | Rudragarh — Desert Fun for Kids & Adults</title>
        <meta name="description" content="Rudragarh is a family-friendly desert resort near Jaisalmer — kids' play zone, ATV rides, camel safari, pool, rain dance, horse riding, and unique stays for the whole family." />
        <meta property="og:title" content="Family Resort in Jaisalmer | Rudragarh — Desert Fun for Kids & Adults" />
        <meta property="og:description" content="Rudragarh is a family-friendly desert resort near Jaisalmer — kids' play zone, ATV rides, camel safari, pool, rain dance, horse riding, and unique stays for the whole family." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/for/family" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pond.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Family Resort in Jaisalmer | Rudragarh" />
        <meta name="twitter:description" content="Kids' play zone, ATV rides, camel safari, pool, and unique stays for the whole family near Sam Sand Dunes." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pond.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/for/family" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pond.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Jaisalmer, Rajasthan</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Family Resort in <span className="text-gold-500">Jaisalmer</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              ATV rides on real dunes. A cave room. Camel safaris. A pool and rain dance floor. Rudragarh keeps every generation busy, together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">For the Whole Family</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              The desert trip kids <span className="text-gold-500">never forget.</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              Jaisalmer is one of India's great family destinations — camel safaris, sand dunes, ancient forts, and a desert culture unlike anywhere else in the country. Rudragarh makes it all accessible, safe, and genuinely exciting for every age group, without losing the sense of adventure that makes the Thar special.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              The property has enough to fill three days without leaving the grounds. A swimming pool and rain dance floor for afternoon energy. A kids' play zone that actually works. ATV rides across the resort's private dunes. Camel safari, horse riding, archery, football — activities that children and parents both enjoy rather than one of them merely tolerating. In the evenings, open-air theatre and cultural programs bring the family together with no screens required.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              And the rooms themselves become part of the adventure. A two-storey bamboo duplex hut. A heritage cottage with pond views. A cave carved into the desert floor. These aren't hotel rooms — they're the kind of places children describe to their school friends on Monday morning.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Built for Families</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Something for <span className="text-gold-500">Everyone</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Where You'll Stay</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms the Kids Will <span className="text-gold-500">Love</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              From a two-storey bamboo duplex to spacious family suites and bookable adjacent cottages — Rudragarh has room for the whole family.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Family Stays at <span className="text-gold-500">Rudragarh</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pond.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Book Your Family Trip</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            The Desert Awaits the Whole Family
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Book your family stay at Rudragarh — where the Thar desert becomes a playground and every room is an adventure in itself.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Family Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Family;
